function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function expandDotted(changes) {
  const root = {};
  Object.entries(changes || {}).forEach(([key, value]) => {
    if (!key.includes('.')) {
      root[key] = value;
      return;
    }
    const parts = key.split('.');
    let cursor = root;
    for (let index = 0; index < parts.length - 1; index += 1) {
      if (!isPlainObject(cursor[parts[index]])) cursor[parts[index]] = {};
      cursor = cursor[parts[index]];
    }
    cursor[parts[parts.length - 1]] = value;
  });
  return root;
}

function mergeDeep(target, changes) {
  const output = { ...(target || {}) };
  Object.entries(changes || {}).forEach(([key, value]) => {
    if (isPlainObject(value) && isPlainObject(output[key])) {
      output[key] = mergeDeep(output[key], value);
    } else {
      output[key] = value;
    }
  });
  return output;
}

function stepId(step) {
  return step && (step.id || step._id) ? String(step.id || step._id) : null;
}

/**
 * Merge a flow-agent patch into the editor's flowData and mark changed steps.
 * Matches backend/src/services/flowAgent/patch.js applyOperations.
 */
export function applyFlowPatch(flowData, patch, highlightIds = []) {
  const next = JSON.parse(JSON.stringify(flowData || { steps: [] }));
  next.steps = next.steps || [];
  const highlighted = new Set((highlightIds || []).map(String));

  (patch?.operations || []).forEach((operation) => {
    if (operation.op === 'update_step') {
      const step = next.steps.find((item) => stepId(item) === String(operation.stepId));
      if (!step) return;
      const changes = expandDotted(operation.changes || {});
      delete changes.flow;
      delete changes.project;
      delete changes.createdBy;
      delete changes._id;
      Object.assign(step, mergeDeep(step, changes));
      return;
    }
    if (operation.op === 'create_step') {
      const step = operation.step || {};
      const id = String(step.id || step.tempId || '');
      if (!id || next.steps.some((item) => stepId(item) === id)) return;
      const maxOrder = next.steps.reduce((max, item) => Math.max(max, item.order || 0), 0);
      next.steps.push({
        id,
        type: step.type,
        name: step.name || 'New step',
        order: step.order || maxOrder + 1,
        active: true,
        nextStepId: step.nextStepId || null,
        position: step.position || { x: 480, y: 180 + (next.steps.length * 40) },
        config: step.config || {},
        variableMapping: step.variableMapping || {},
        variableOperations: step.variableOperations || []
      });
      return;
    }
    if (operation.op === 'rewire') {
      const step = next.steps.find((item) => stepId(item) === String(operation.stepId));
      if (!step) return;
      if (Object.prototype.hasOwnProperty.call(operation, 'nextStepId')) {
        step.nextStepId = operation.nextStepId || null;
      }
      if (
        Object.prototype.hasOwnProperty.call(operation, 'trueNextStepId')
        || Object.prototype.hasOwnProperty.call(operation, 'falseNextStepId')
      ) {
        step.config = step.config || {};
        step.config.branch = step.config.branch || {};
        if (Object.prototype.hasOwnProperty.call(operation, 'trueNextStepId')) {
          step.config.branch.trueNextStepId = operation.trueNextStepId || null;
        }
        if (Object.prototype.hasOwnProperty.call(operation, 'falseNextStepId')) {
          step.config.branch.falseNextStepId = operation.falseNextStepId || null;
        }
      }
    }
  });

  next.steps = next.steps.map((step) => ({
    ...step,
    agentEdited: highlighted.has(stepId(step))
  }));
  return next;
}

export function stripAgentEdited(steps) {
  return (steps || []).map((step) => {
    if (!step.agentEdited) return step;
    const cleaned = { ...step };
    delete cleaned.agentEdited;
    return cleaned;
  });
}
